import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { environment } from '../../../../environments/environment';
import { UnreadMessagesService } from '../../../domain/service/unreadMessages.service';

interface MessageAttachment {
  id: number;
  fileName: string;
  contentType: string | null;
  fileSize: number | null;
  url: string | null;
}

interface AccountMessage {
  id: number;
  accountId: number;
  sender: string;
  subject: string;
  body: string;
  read: boolean;
  createdAt: string;
  /** Staff correspondence is HTML (inline images already resolved to links); system notices are text. */
  html: boolean;
  attachments: MessageAttachment[];
}

/** Taxpayer inbox: system notices (new return, reminders, payments) posted by the backend. */
@Component({
  selector: 'app-inbox',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatCheckboxModule, MatButtonModule],
  templateUrl: './inbox.component.html',
  styleUrl: './inbox.component.css',
})
export class InboxComponent implements OnInit {
  messages: AccountMessage[] = [];
  selected: AccountMessage | null = null;
  checked = new Set<number>();
  loading = false;
  private base = `${environment.apiUrl}/external/messages`;

  constructor(private http: HttpClient, private unread: UnreadMessagesService) {}

  size(bytes: number | null): string {
    if (!bytes) return '';
    return bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading = true;
    // The account comes from the login token on the server; no id in the URL.
    this.http.get<AccountMessage[]>(this.base).subscribe({
      next: (m) => { this.messages = m ?? []; this.loading = false; this.checked.clear(); this.syncBadge(); },
      error: () => { this.messages = []; this.loading = false; },
    });
  }

  open(message: AccountMessage): void {
    this.selected = message;
    if (!message.read) {
      this.http.put(`${this.base}/${message.id}/read`, {}).subscribe({ next: () => { message.read = true; this.syncBadge(); }, error: () => {} });
    }
  }

  back(): void { this.selected = null; }

  preview(m: AccountMessage): string {
    const text = m.html ? (m.body || '').replace(/<[^>]+>/g, ' ') : (m.body || '');
    const t = text.replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
    return t.length > 110 ? t.slice(0, 110) + '…' : t;
  }

  /** "48 mins" / "3 hrs" today, otherwise "Sep 18" (with the year when not this year). */
  when(m: AccountMessage): string {
    const d = new Date(m.createdAt); const now = new Date();
    const mins = Math.max(1, Math.round((now.getTime() - d.getTime()) / 60000));
    if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'}`;
    if (d.toDateString() === now.toDateString()) { const h = Math.round(mins / 60); return `${h} hr${h === 1 ? '' : 's'}`; }
    return d.toLocaleDateString('en-US', d.getFullYear() === now.getFullYear() ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
  }

  get unreadCount(): number { return this.messages.filter((m) => !m.read).length; }
  get allChecked(): boolean { return this.messages.length > 0 && this.checked.size === this.messages.length; }

  toggle(m: AccountMessage, on: boolean): void { on ? this.checked.add(m.id) : this.checked.delete(m.id); }
  toggleAll(on: boolean): void { this.checked = on ? new Set(this.messages.map((m) => m.id)) : new Set(); }

  remove(m: AccountMessage, ev?: Event): void {
    ev?.stopPropagation();
    this.http.delete(`${this.base}/${m.id}`).subscribe({ next: () => this.dropLocal([m.id]), error: () => {} });
  }

  removeChecked(): void {
    const ids = Array.from(this.checked);
    if (!ids.length) return;
    this.http.post(`${this.base}/delete`, { ids }).subscribe({ next: () => this.dropLocal(ids), error: () => {} });
  }

  private dropLocal(ids: number[]): void {
    this.messages = this.messages.filter((m) => !ids.includes(m.id));
    ids.forEach((id) => this.checked.delete(id));
    if (this.selected && ids.includes(this.selected.id)) this.selected = null;
    this.syncBadge();
  }

  private syncBadge(): void { this.unread.count.set(this.unreadCount); }
}
